import appConfiguration from "@/components/cfg/AppCfg";
import DataTableCellField from "@/components/data-table/components/DataTableCellField";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { QueryTable } from "@/components/query-table/QueryTable";
import MonthYearNavigation from "@/components/MonthYearNavigation";
import { Alert, AlertTitle } from "@/components/ui/alert";
import {
  Module,
  PATH_PROJECT_CONTRACTOR_VISIT,
  YM_DATE_FORMAT,
} from "@/constants";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { getFirstDayOfMonth } from "@/lib/date";
import { noop } from "@/lib/noop";
import ContractorVisitsEditorDirectoryForm from "@/modules/projects/components/contractors-visits/components/ContractorVisitsEditorDirectoryForm";
import { useCanEditContractorVisitFields } from "@/modules/projects/components/contractors-visits/hooks/useCanEditContractorVisitFields";
import ProjectStoreSelect from "@/modules/projects/components/ProjectStoreSelect";
import { useProjectBreadcrumb } from "@/modules/projects/hooks/useProjectBreadcrumb";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { ProjectService } from "@/modules/projects/service/project";
import { ContractorVisitsService } from "@/modules/projects/services/contractorVisits";
import {
  basicEditorContractorVisit,
  fullEditorContractorVisit,
  VISIT_STATUS,
  DEVICE_TYPE,
} from "@/schemas/contractorVisit";
import { SETTLEMENT_TYPE } from "@/schemas/commonFields/settlementType";
import { SuccessFetchListResponse } from "@/services/api";
import { ReadContractorVisit } from "@/types/contractorVisit";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { Row } from "@tanstack/react-table";
import { formatDate } from "date-fns";
import { exists } from "i18next";
import {
  formatDateTime,
  formatTime,
  formatNumber,
} from "@/lib/utils/formatters";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { ContractorVisitsSummary } from "./ContractorVisitsSummary";
import Title from "@/components/Title";
import { DateTimePickerField } from "@/components/DateTimePickerField";
import { TimeInputField } from "@/components/TimeInputField";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { ProjectDetailsAndNavigation } from "../../ProjectDetailsAndNavigation";

export const ContractorVisitsEditorDirectory = memo(() => {
  const { t } = useTranslation();
  const {
    canEditDateTimeRange,
    canEditRrPlannedDuration,
    canEditSuggestedTimes,
  } = useCanEditContractorVisitFields();
  const permissions = useModulePermissions(Module.ContractorVisits);
  const name = Module.ContractorVisits;
  const projectId = useProjectId();

  const hasFullAccess = permissions.contractor_visits_read_full === true;

  const schema = hasFullAccess
    ? fullEditorContractorVisit
    : basicEditorContractorVisit;

  const columnOverrides = useMemo(
    () => ({
      ...(permissions.contractor_visits_read_full && {
        pr_planned_duration: {
          id: "pr_planned_duration",
          cell: ({ row }: { row: Row<ReadContractorVisit> }) =>
            canEditRrPlannedDuration(row.original) ? (
              <DataTableCellField
                name={`entries.${row.original.id}.pr_planned_duration`}
                type="number"
              />
            ) : (
              formatNumber(row.original.pr_planned_duration)
            ),
        },
        start_datetime: {
          id: "start_datetime",
          cell: ({ row }: { row: Row<ReadContractorVisit> }) =>
            canEditDateTimeRange(row.original) ? (
              <div className="flex items-center justify-center h-full py-[2px] px-2">
                <DateTimePickerField
                  name={`entries.${row.original.id}.start_datetime`}
                  className="h-full rounded-sm focus-visible:ring-offset-0 border-input bg-background hover:bg-accent hover:text-accent-foreground text-xs font-normal shadow-none"
                />
              </div>
            ) : (
              formatDateTime(row.original.start_datetime)
            ),
        },
        end_datetime: {
          id: "end_datetime",
          cell: ({ row }: { row: Row<ReadContractorVisit> }) =>
            canEditDateTimeRange(row.original) ? (
              <div className="flex items-center justify-center h-full py-[2px] px-2">
                <DateTimePickerField
                  name={`entries.${row.original.id}.end_datetime`}
                  className="h-full rounded-sm focus-visible:ring-offset-0 border-input bg-background hover:bg-accent hover:text-accent-foreground text-xs font-normal shadow-none"
                />
              </div>
            ) : (
              formatDateTime(row.original.end_datetime)
            ),
        },
      }),
      suggested_start_time: {
        id: "suggested_start_time",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) =>
          canEditSuggestedTimes(row.original) ? (
            <div className="flex items-center justify-center h-full py-[2px] px-2">
              <TimeInputField
                name={`entries.${row.original.id}.suggested_start_time`}
              />
            </div>
          ) : (
            formatTime(row.original.suggested_start_time)
          ),
      },
      suggested_end_time: {
        id: "suggested_end_time",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) =>
          canEditSuggestedTimes(row.original) ? (
            <div className="flex items-center justify-center h-full py-[2px] px-2">
              <TimeInputField
                name={`entries.${row.original.id}.suggested_end_time`}
              />
            </div>
          ) : (
            formatTime(row.original.suggested_end_time)
          ),
      },
      status: {
        id: "status",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          const key = `projectContractorVisits.enum.status.${row.original.status}`;
          if (!exists(key)) {
            return row.original.status;
          }
          return t(key);
        },
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: VISIT_STATUS,
              translationKey: "projectContractorVisits.enum.status",
            },
          },
        },
      },
      device_type: {
        id: "device_type",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          const key = `projectContractorVisits.enum.deviceType.${row.original.device_type}`;
          if (!exists(key)) {
            return row.original.device_type;
          }
          return t(key);
        },
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: DEVICE_TYPE,
              translationKey: "projectContractorVisits.enum.deviceType",
            },
          },
        },
      },
      settlement_type: {
        id: "settlement_type",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          const key = `settlementType.${row.original.settlement_type}`;
          if (!exists(key)) {
            return row.original.settlement_type;
          }
          return t(key);
        },
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: SETTLEMENT_TYPE,
              translationKey: "settlementType",
            },
          },
        },
      },
      project_name: {
        id: "project_name",
        enableColumnFilter: false,
        enableSorting: false,
      },
      store_name: {
        id: "store_name",
        enableColumnFilter: false,
        enableSorting: false,
      },
    }),
    [
      t,
      canEditDateTimeRange,
      canEditRrPlannedDuration,
      canEditSuggestedTimes,
      permissions.contractor_visits_read_full,
    ],
  );

  const [selectedStoreId, setSelectedStoreId] = useState<string | undefined>(
    undefined,
  );
  const [selectedMonthYear, setSelectedMonthYear] = useState(
    getFirstDayOfMonth(new Date()),
  );
  const dateString = useMemo(
    () => formatDate(selectedMonthYear, YM_DATE_FORMAT),
    [selectedMonthYear],
  );
  const options = useCreateDataTableOptions<ReadContractorVisit>(name);

  const queryKey = useDynamicQueryParams(name, options, {
    dateString,
    projectId,
    selectedStoreId,
  });

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadContractorVisit>) =>
      lastPage.last_item,
    [],
  );

  const fetchList = useCallback(
    async ({ pageParam }: { pageParam: string | undefined }) =>
      ContractorVisitsService.getList({
        lastItem: pageParam,
        filters: options.filters,
        limit: appConfiguration.paginationLimit,
        queryParams: {
          project_id: projectId,
          store_id: selectedStoreId as string,
          period: dateString,
        },
      }),
    [dateString, projectId, selectedStoreId, options.filters],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: fetchList,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: Boolean(projectId && selectedStoreId),
  });

  const onMonthChange = useCallback(
    (date: Date) => {
      setSelectedMonthYear(date);
    },
    [setSelectedMonthYear],
  );

  const queryProject = () => ProjectService.get(projectId);
  const projectQuery = useQuery({
    queryKey: [Module.Projects, projectId],
    queryFn: queryProject,
  });

  const breadcrumbItems = useProjectBreadcrumb(projectQuery, [
    {
      label: t("projects.breadcrumb.contractorsVisitsEditorDirectory"),
    },
  ]);

  const initialColumnVisibility = useMemo(
    () => ({
      client_ref_number: false,
      client_name: false,
      client_tax_number: false,
      store_ref_number: false,
      network: false,
      city: false,
      street_name: false,
      street_number: false,
      postal_code: false,
      contractor_phone_number: false,
      contractor_full_name_and_personal_id_number: false,
      geolocalization_enabled: false,
      editable_by_pr: false,
      hours_editable_by_contractor: false,
      confirmable_by_contractor: false,
      edit_allowed: false,
      regional_director_name: false,
      regional_representative_name: false,
      supervisor_name: false,
      store_coordinator_name: false,
      invoicing_company: false,
      payroll_type: false,
      project_type: false,
      suggested_start_time: false,
      suggested_end_time: false,
    }),
    [],
  );

  const tableFeatures = useMemo(
    () => ({
      initialColumnVisibility,
      exportSettings: {
        moduleName: Module.ContractorVisitsSummary,
        queryParams: {
          project_id: projectId,
          store_id: selectedStoreId as string,
          period: dateString,
        },
      },
    }),
    [dateString, initialColumnVisibility, projectId, selectedStoreId],
  );
  return (
    <>
      <DataTableQueryContextProvider value={{ query }}>
        <QueryTable<ReadContractorVisit>
          schema={schema}
          name={name}
          query={query}
          options={options}
          columnOverrides={columnOverrides}
          translationKey="projectContractorVisits"
          tableFeatures={tableFeatures}
          pathBuilder={(row) =>
            generatePath(PATH_PROJECT_CONTRACTOR_VISIT, {
              contractorVisitId: row.id,
            })
          }
        >
          <QueryTable.Header
            breadcrumbLabel="projectContractorVisits.title.multiple"
            breadcrumbs={breadcrumbItems}
          />
          <Title>{t("projectContractorVisits.editVisitsTitle")}</Title>
          <ProjectDetailsAndNavigation />
          <div className="flex flex-row gap-2 justify-between items-center py-2">
            <QueryTable.Menubar className="mb-0 grow">
              <QueryTable.Menubar.Tools />
              <QueryTable.Menubar.Columns />
              <QueryTable.Menubar.Settings />
            </QueryTable.Menubar>

            <ProjectStoreSelect
              name="store"
              projectId={projectId}
              enabled={Boolean(projectId)}
              value={selectedStoreId}
              onChange={setSelectedStoreId}
              onBlur={noop}
              className="md:min-w-[350px]"
              showEntityLink={false}
            />

            <MonthYearNavigation
              value={selectedMonthYear}
              onMonthChange={onMonthChange}
              className="shrink-0 grow-0"
              popoverClassName="z-100"
              disabled={query.isFetching}
            />
          </div>
          {!selectedStoreId && (
            <Alert>
              <AlertTitle>
                {t("projectContractorVisits.pleaseSelectStore")}
              </AlertTitle>
            </Alert>
          )}
          {selectedStoreId && <ContractorVisitsEditorDirectoryForm />}
        </QueryTable>
      </DataTableQueryContextProvider>
      {selectedStoreId && (
        <>
          <Title level={4}>{t("projectContractorVisits.summary.title")}</Title>
          <ContractorVisitsSummary
            projectId={projectId}
            storeId={selectedStoreId}
            period={dateString}
            hasFullAccess={hasFullAccess}
          />
        </>
      )}
    </>
  );
});
